import React from "react";
import { Link } from "react-router-dom";
import { FiAlertCircle, FiArrowLeft, FiArrowRight, FiCheckCircle, FiDownload, FiInfo } from "react-icons/fi";
import PayoutBulkUploadFlowSteps from "./PayoutBulkUploadFlowSteps";
import PayoutDataTable from "./PayoutDataTable";
import { readSessionJson } from "./api/payoutMutationHelpers";
import { VerifiedBeneficiaryRow } from "./api/payoutBulkValidationHelpers";

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-[#E0E5F2] py-3 text-sm last:border-b-0">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <span className="font-semibold text-[#1B163B]">{value}</span>
    </div>
  );
}

function PayoutBeneficiaryBulkUploadReview() {
  const draft = readSessionJson<{
    fileName?: string;
    rows?: VerifiedBeneficiaryRow[];
    headersDetected?: string[];
  }>("payoutBulkBeneficiaryDraft", {});

  const fileName = draft.fileName || "payout-beneficiary-bulk-upload-sample.csv";
  const rowsData: VerifiedBeneficiaryRow[] = Array.isArray(draft.rows) ? draft.rows : [];

  const totalRows = rowsData.length;
  const validRowsCount = rowsData.filter((r) => r.isValid).length;
  const errorRowsCount = totalRows - validRowsCount;

  const formattedRows = rowsData.map((row) => [
    row.payout_account_id || "Operating Account",
    <div>
      <div className="font-semibold text-[#1B163B]">{row.beneficiary_name || "—"}</div>
      {(row.email || row.mobile_number) && (
        <div className="text-xs text-[#505E8E]">
          {row.mobile_number ? `Mob: ${row.mobile_number}` : ""}
          {row.email ? ` • ${row.email}` : ""}
        </div>
      )}
    </div>,
    <span className="rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
      {row.category || "Vendor"}
    </span>,
    row.ifsc_code ? `${row.ifsc_code.substring(0, 4)} Bank` : "Bank",
    row.account_number ? `**** ${row.account_number.slice(-4)}` : "—",
    row.ifsc_code || "—",
    row.isValid ? (
      <span className="inline-flex items-center gap-1 rounded-[5px] bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#16A34A]">
        <FiCheckCircle /> Ready
      </span>
    ) : (
      <span
        title={row.errors.join("; ")}
        className="inline-flex items-center gap-1 rounded-[5px] bg-[#FEE2E2] px-2 py-1 text-xs font-semibold text-[#DC2626]"
      >
        <FiAlertCircle /> {row.errors[0] || "Invalid"}
      </span>
    ),
  ]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Review Beneficiary Upload
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Review parsed beneficiaries and field validation before verification.
        </p>
      </div>

      <PayoutBulkUploadFlowSteps activeStep={2} />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Parsed Beneficiary Preview
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {validRowsCount} rows validated. {errorRowsCount} error(s) found.
              </p>
            </div>
            <button className="flex h-10 items-center gap-3 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]">
              <FiDownload />
              Export validation report
            </button>
          </div>

          <PayoutDataTable
            minWidth="min-w-[1000px]"
            columns={[
              "Payout Account",
              "Beneficiary",
              "Category",
              "Bank",
              "Account Number",
              "IFSC",
              "Validation",
            ]}
            rows={formattedRows}
          />

          <div className="mt-5 flex gap-3 rounded-[6px] bg-[#1B163B0D] px-5 py-4 text-sm font-medium text-[#1B163B]">
            <FiInfo className="mt-0.5 shrink-0 text-lg" />
            Beneficiaries will be submitted together after Google Authenticator verification.
          </div>

          <div className="mt-6 flex items-center justify-between">
            <Link
              to="/payouts/beneficiaries/bulk-upload"
              className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft />
              Back
            </Link>
            <Link
              to="/payouts/beneficiaries/bulk-upload/verify"
              className={`flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white ${
                errorRowsCount > 0 ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              Continue to Verification
              <FiArrowRight />
            </Link>
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Upload Summary
          </h2>
          <div className="my-5 h-px bg-[#E0E5F2]" />
          <SummaryRow label="File Name" value={fileName} />
          <SummaryRow label="Total Rows" value={String(totalRows)} />
          <SummaryRow label="Valid Rows" value={String(validRowsCount)} />
          <SummaryRow label="Rows with Errors" value={String(errorRowsCount)} />
          <SummaryRow label="Duplicate Accounts" value="0" />
          {errorRowsCount === 0 ? (
            <div className="mt-5 rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-medium text-[#166534]">
              This file is ready to be submitted for verification.
            </div>
          ) : (
            <div className="mt-5 rounded-[6px] bg-[#FEE2E2] px-4 py-3 text-sm font-medium text-[#B91C1C]">
              Please fix the errors in your CSV file before proceeding.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default PayoutBeneficiaryBulkUploadReview;

